import { useLayoutEffect, useState } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { cancelRender, continueRender, delayRender } from 'remotion';
import { AnyOverlay, TextOverlay } from '../../models/Post';
import { RemotionTextOverlay } from './RemotionTextOverlay';

/** Fit the complete text before animating it, using the same renderer as export. */
export function useVideoTextScale(
  overlay: AnyOverlay,
  width: number,
  height: number,
  desiredScale: number | undefined,
  layoutEngine?: 'fabric' | 'remotion',
  sourceLayout?: { width: number; height: number; referenceWidth: number },
): number | undefined {
  const isText = !overlay.type || overlay.type === 'text';
  const [fit, setFit] = useState<{ key: string; scale: number }>();
  const key = isText && desiredScale
    ? JSON.stringify([overlay, width, height, desiredScale, layoutEngine, sourceLayout])
    : '';

  useLayoutEffect(() => {
    if (!isText || !desiredScale || typeof document === 'undefined') return;
    const textOverlay = overlay as TextOverlay;
    const maxWidth = (textOverlay.width ?? (100 - textOverlay.position.x)) / 100 * width;
    let maxHeight = (100 - textOverlay.position.y) / 100 * height;
    if (maxWidth <= 0 || maxHeight <= 0) return;

    const handle = delayRender('Fit carousel text to video');
    let disposed = false;
    const host = document.createElement('div');
    host.style.cssText = 'position:fixed;left:-100000px;top:0;visibility:hidden;pointer-events:none;';
    document.body.appendChild(host);

    const measure = (scale: number, canvasWidth: number, canvasHeight: number) => {
      const boxWidth = (textOverlay.width ?? (100 - textOverlay.position.x)) / 100 * canvasWidth;
      // React escapes all text. Measuring the real markup also handles rich text,
      // padding, paragraph spacing, CJK wrapping and per-line backgrounds.
      host.innerHTML = renderToStaticMarkup(
        <RemotionTextOverlay
          overlay={{ ...textOverlay, position: { x: 0, y: 0 }, rotation: 0 }}
          containerWidth={canvasWidth}
          containerHeight={canvasHeight}
          scaleFactor={scale}
          layoutEngine={layoutEngine}
          measureOnly
        />,
      );
      const block = host.firstElementChild as HTMLElement;
      block.style.width = `${boxWidth}px`;
      block.style.height = 'auto';
      block.style.top = '0';
      block.style.transform = 'none';
      // Text can paint outside its line box when line-height is tight.
      const range = document.createRange();
      range.selectNodeContents(block);
      const textBounds = range.getBoundingClientRect();
      const box = block.getBoundingClientRect();
      return {
        width: block.scrollWidth,
        height: Math.max(block.scrollHeight, box.height, textBounds.bottom - box.top),
      };
    };

    const fits = (scale: number) => {
      const measured = measure(scale, width, height);
      return measured.width <= maxWidth + 0.5 && measured.height <= maxHeight + 0.5;
    };

    // Mount once to request the fonts used by every styled run before measuring.
    fits(desiredScale);
    void (async () => {
      try {
        if (document.fonts) await document.fonts.ready;
        if (disposed) return;
        if (sourceLayout) {
          // The carousel's actual text occupancy is the reference, including
          // wrapping, styled runs, line height and background padding. Do not
          // infer barriers from unrelated SVG/image bounding boxes.
          const original = measure(
            sourceLayout.width / sourceLayout.referenceWidth,
            sourceLayout.width, sourceLayout.height,
          );
          maxHeight = Math.min(maxHeight, original.height * height / sourceLayout.height);
        } else if (textOverlay.height && textOverlay.height > 0) {
          maxHeight = Math.min(maxHeight, textOverlay.height / 100 * height);
        }
        let scale = desiredScale;
        if (!fits(scale)) {
          let low = 0;
          let high = scale;
          for (let i = 0; i < 12; i++) {
            const mid = (low + high) / 2;
            if (fits(mid)) low = mid;
            else high = mid;
          }
          scale = Math.max(low, desiredScale / 4096);
        }
        setFit({ key, scale });
      } catch (error) {
        cancelRender(error);
      } finally {
        host.remove();
        continueRender(handle);
      }
    })();
    return () => {
      disposed = true;
      host.remove();
      continueRender(handle);
    };
    // The serialized key includes the complete overlay, including styled runs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, isText]);

  if (!isText || !desiredScale) return undefined;
  return fit?.key === key ? fit.scale : desiredScale;
}
